import get from 'lodash/get';
import { isUserAmTeam, isUserAmTeamGodMode, isUserBeta, isUserOmAlpha } from './user';
import appData from './appData';

/**
 * Function to check against user permissions/features.
 *
 * @param {Object} user The user object from global state.
 * @param {string} category The property to look inside for permissions (haystack).
 * @param {string} permission The needle to look for.
 * @param {Object} resource The resource data object to use for permissions checks.
 *
 * @returns {boolean} True if the user is allowed.
 */
export const allowed = (user, category, permission, resource) => {
	if ('roles' === category) {
		// If the user does not have a role, return false.
		if (!(user.role in roles)) {
			return false;
		}

		// If we don't have a resource, do a quick check for the exact permission.
		if (!resource) {
			return roles[user.role].includes(permission);
		}

		// If the exact permission exists, then return true.
		if (roles[user.role].includes(permission)) {
			return true;
		}

		// Check if the user has a conditional permission.
		const permissions = roles[user.role].filter((p) => p.startsWith(permission));
		const passed = [];
		if (permissions.length) {
			permissions.forEach((p) => {
				const attribute = p.replace(permission + '.', '');
				passed.push(resource[attribute]);
			});

			return passed.every((p) => p);
		}

		return false;
	}

	if ('amteam' === category) {
		return get(user, 'isAmTeam');
	}

	if ('beta' === category) {
		return get(user, 'inBeta') || get(user, 'beta.enabled');
	}

	let property = get(user, category, []);

	if ('preReleaseFeatures' === category && (!property || !Array.isArray(property))) {
		property = appData.get('preReleaseFeatures', []);
	}

	if ('features' === category && (!property || !Array.isArray(property))) {
		// Try to find features in the app data.
		property = appData.get('features', []);
	}

	return property.includes(permission);
};

/**
 * Determines if the passed user is in the OM Alpha group.
 *
 * @since 0.1.30
 *
 * @param {Object} user The user object.
 *
 * @returns {boolean} True if user in AM Team group.
 */
export const allowedInOmAlpha = (user) => {
	return isUserOmAlpha(user);
};

/**
 * Determines if the passed user is in the AM Team group.
 *
 * @param {Object} user The user object.
 * @param {Object} includeGodMode Whether to also check if am-team in godmode.
 *
 * @returns {boolean} True if user in AM Team group.
 */
export const allowedInAmTeam = (user, includeGodMode = false) => {
	if (includeGodMode && isUserAmTeamGodMode(user)) {
		return true;
	}

	return isUserAmTeam(user);
};

/**
 * Determines if the passed user is in the AM Team group and/or the beta group.
 *
 * @param {Object} user The user object.
 *
 * @returns {boolean} True if user in AM Team group and/or the beta group.
 */
export const allowedInBeta = (user) => {
	return isUserBeta(user) || isUserAmTeam(user);
};

/**
 * Determines if the passed user is in the AM Team group and/or the beta group.
 *
 * @param {Object} user    The user object.
 * @param {string} feature The feature slug to check for access.
 *
 * @returns {boolean} True if user in AM Team group and/or the beta group.
 */
export const allowedInPreRelease = (user, feature) => {
	return allowed(user, 'preReleaseFeatures', feature);
};

const roles = {
	owner: [
		'analytics.view',
		'publish.status',
		'publish.embed',
		'site.edit',
		'site.connect',
		'site.create',
		'folder.edit',
		'folder.connect',
		'folder.create',
		'folder.delete',
		'campaign.edit',
		'campaign.create',
		'campaign.delete',
		'campaign.archive',
		'bulk.select',
	],
	admin: [
		'analytics.view',
		'publish.status',
		'publish.embed',
		'site.edit',
		'site.connect',
		'site.create',
		'folder.edit',
		'folder.connect',
		'folder.create',
		'folder.delete',
		'campaign.edit',
		'campaign.create',
		'campaign.delete',
		'campaign.archive',
		'bulk.select',
	],
	manager: [
		'analytics.view',
		'publish.status',
		'publish.embed',
		'site.connect',
		'folder.connect',
		'folder.create',
		'folder.edit',
		'campaign.edit',
		'campaign.create',
		'campaign.delete',
		'campaign.archive',
		'bulk.select',
	],
	author: ['publish.embed', 'analytics.view', 'campaign.edit.can_edit', 'campaign.create', 'bulk.select'],
	viewer: [],
};
